import React, { useEffect, useMemo, useState } from "react";
import { Typography } from "antd";
import { toast } from "react-toastify";
import MyDevices, {
  DeviceOrder,
} from "../../../components/Settings/Subscription/MyDevices";
import OrderReview from "../../../components/Settings/Subscription/OrderReview";
import ViewCard from "../../../components/Settings/Subscription/PaymentCard/ViewCard";
import ProductDetail from "../../../components/Settings/Subscription/ProductDetail";
import ShippingForm from "../../../components/Settings/Subscription/ShippingForm";
import { useUserProfileGate } from "../../../hooks/useUserProfileGate";
import { homePhoneService } from "../../../services/homePhoneService";
import {
  HomePhoneProduct,
  HomePhoneShippingDetails,
} from "../../../types/homePhone";
import { customLogger } from "../../../utils/Logger";
import "./index.scss";

const { Text, Title } = Typography;

type RightView = "home-phone" | "my-device" | "view-card";
type HomePhoneStep = "details" | "shipping" | "review";

interface SettingsRightSectionProps {
  rightView: RightView;
  homePhoneProduct: HomePhoneProduct | null;
  onOpenHomePhone: () => void;
  onOpenMyDevice: () => void;
}

const getErrorMessage = (error: unknown, fallback: string) => {
  return error instanceof Error ? error.message : fallback;
};

const SettingsRightSection: React.FC<SettingsRightSectionProps> = ({
  rightView,
  homePhoneProduct,
  onOpenHomePhone,
  onOpenMyDevice,
}) => {
  const { gate } = useUserProfileGate();
  const [detailStep, setDetailStep] = useState<HomePhoneStep>("details");
  const [selectedProduct, setSelectedProduct] =
    useState<HomePhoneProduct | null>(homePhoneProduct);
  const [shippingDetails, setShippingDetails] =
    useState<HomePhoneShippingDetails | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [paymentCardId, setPaymentCardId] = useState<string>();
  const [showCardView, setShowCardView] = useState(false);
  const [orders, setOrders] = useState<DeviceOrder[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [focusOrderId, setFocusOrderId] = useState<string | null>(null);
  const [myDeviceFromPurchase, setMyDeviceFromPurchase] = useState(false);

  useEffect(() => {
    if (!homePhoneProduct) {
      setSelectedProduct(null);
      setDetailStep("details");
      setShippingDetails(null);
      return;
    }

    if (rightView !== "home-phone") return;

    setSelectedProduct(homePhoneProduct);
    setDetailStep("details");
    setShippingDetails(null);
    setShowCardView(false);
    setFocusOrderId(null);

    let isMounted = true;
    setDetailLoading(true);

    homePhoneService
      .getProductDetails(homePhoneProduct.id)
      .then((details) => {
        if (isMounted) {
          setSelectedProduct({ ...homePhoneProduct, ...details });
        }
      })
      .catch((error: unknown) => {
        customLogger(error);
        toast.error(getErrorMessage(error, "Unable to load product details."));
      })
      .finally(() => {
        if (isMounted) {
          setDetailLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [homePhoneProduct, rightView]);

  useEffect(() => {
    if (rightView !== "my-device") {
      setMyDeviceFromPurchase(false);
      return;
    }

    let isMounted = true;
    setOrdersLoading(true);

    Promise.all([
      homePhoneService.getMyOrders(),
      homePhoneService.getProducts(),
    ])
      .then(([orderList, productList]) => {
        if (!isMounted) return;

        const productMap = new Map(
          productList.map((product) => [product.id, product]),
        );
        const mergedOrders: DeviceOrder[] = orderList.map((order) => ({
          ...order,
          product: productMap.get(order.productId),
        }));
        setOrders(mergedOrders);
      })
      .catch((error: unknown) => {
        customLogger(error);
        toast.error(getErrorMessage(error, "Unable to load device orders."));
      })
      .finally(() => {
        if (isMounted) {
          setOrdersLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [rightView]);

  const orderTotals = useMemo(() => {
    const price = selectedProduct?.price || 0;
    const shippingCharge = selectedProduct?.shippingCharge || 0;
    const activationCharge = selectedProduct?.activationCharge || 0;

    return {
      price,
      shippingCharge,
      activationCharge,
      total: price + shippingCharge + activationCharge,
    };
  }, [selectedProduct]);

  const handleStartShipping = async () => {
    const isAllowed = await gate();
    if (!isAllowed) return;
    setDetailStep("shipping");
  };

  const handleShippingSubmit = (values: HomePhoneShippingDetails) => {
    setShippingDetails(values);
    setDetailStep("review");
  };

  const handleCreateOrder = async () => {
    if (!selectedProduct || !shippingDetails) return;

    setSubmitting(true);
    try {
      const order = await homePhoneService.createOrder(selectedProduct.id, {
        shippingDetails,
      });
      setFocusOrderId(order.id);
      setMyDeviceFromPurchase(true);
      toast.success("Home phone order created successfully.");
      onOpenMyDevice();
    } catch (error: unknown) {
      customLogger(error);
      toast.error(getErrorMessage(error, "Unable to create home phone order."));
    } finally {
      setSubmitting(false);
    }
  };

  const refreshOrders = async () => {
    const [orderList, productList] = await Promise.all([
      homePhoneService.getMyOrders(),
      homePhoneService.getProducts(),
    ]);
    const productMap = new Map(
      productList.map((product) => [product.id, product]),
    );
    const mergedOrders: DeviceOrder[] = orderList.map((order) => ({
      ...order,
      product: productMap.get(order.productId),
    }));
    setOrders(mergedOrders);
  };

  const handleActivateDevice = async (
    orderId: string,
    values: { macAddress: string },
  ) => {
    await homePhoneService.activateOrder(orderId, values);
    toast.success("Home phone device activated successfully.");
    await refreshOrders();
  };

  const renderHomePhone = () => {
    if (!selectedProduct) {
      return (
        <div className="settings-right-empty">
          <img src="/assets/images/graphic.png" alt="Efone Web" />
          <Title level={3}>Efone Web</Title>
          <Text>
            Select a HomePhone product from Subscription to review details.
          </Text>
        </div>
      );
    }

    return (
      <>
        {showCardView && paymentCardId ? (
          <div className="settings-right-panel">
            <ViewCard
              handleBackToPrevious={() => setShowCardView(false)}
              id={paymentCardId}
            />
          </div>
        ) : (
          <>
            {detailStep === "details" && (
              <ProductDetail
                product={selectedProduct}
                loading={detailLoading}
                onGetDevice={handleStartShipping}
              />
            )}

            {detailStep === "shipping" && (
              <ShippingForm
                product={selectedProduct}
                initialValues={shippingDetails}
                onBack={() => setDetailStep("details")}
                onSubmit={handleShippingSubmit}
              />
            )}

            {detailStep === "review" && shippingDetails && (
              <OrderReview
                product={selectedProduct}
                shippingDetails={shippingDetails}
                totals={orderTotals}
                submitting={submitting}
                onBack={() => setDetailStep("details")}
                onEditShipping={() => setDetailStep("shipping")}
                onConfirm={handleCreateOrder}
                onViewCard={() => setShowCardView(true)}
                setPaymentCardId={setPaymentCardId}
              />
            )}
          </>
        )}
      </>
    );
  };

  return (
    <div className="settings-right-section">
      {rightView === "home-phone" && renderHomePhone()}
      {rightView === "my-device" && (
        <MyDevices
          orders={orders}
          ordersLoading={ordersLoading}
          focusOrderId={focusOrderId}
          showBack={myDeviceFromPurchase}
          onBack={onOpenHomePhone}
          onActivateDevice={handleActivateDevice}
        />
      )}
      {rightView === "view-card" && paymentCardId && (
        <div className="settings-right-panel">
          <ViewCard handleBackToPrevious={onOpenHomePhone} id={paymentCardId} />
        </div>
      )}
    </div>
  );
};

export default SettingsRightSection;
